import { isObject } from "./isObject";

type Some<T> = {
  type: "some";
  value: T;
};

type None = {
  type: "none";
};

type RefObject<T> = {
  current: T;
};

export type Maybe<T> = Some<T> | None;

const noneSingleton = Object.freeze({ type: "none" }) as None;

const none = (): None => {
  return noneSingleton;
};

const some = <T>(value: T): Some<T> => {
  return { type: "some", value };
};

const isMaybe = <T>(obj: any): obj is Maybe<T> => {
  return isObject(obj) && ((obj.type === "some" && "value" in obj) || obj.type === "none");
};

const isSome = <T>(obj: any): obj is Some<T> => {
  return isObject(obj) && obj.type === "some" && "value" in obj;
};

const isNone = (obj: any): obj is None => {
  return isObject(obj) && obj.type === "none";
};

const map = <T, U>(maybe: Maybe<T> | T | undefined, fn: (value: T) => U): Maybe<U> => {
  maybe = from(maybe);

  switch (maybe.type) {
    case "none":
      return none();
    case "some":
      return some(fn(maybe.value));
  }
};

/**
 * Converts an Maybe into a RefObject of ready data. Useful when you have a
 * ref value that you expect to be "some", but don't want to have to check
 * on every change.
 */
const toRef = <T>(fn: () => Maybe<T>): RefObject<T> => {
  return {
    get current() {
      const obj = fn();

      if (maybe.is.none(obj)) {
        throw new Error("maybe.toRef: Expected some, got none");
      }

      return obj.value;
    },
  };
};

const from = <T>(value?: T | Maybe<T>): Maybe<T> => {
  /**
   * Use `==` instead of `===` to check for both `null` and `undefined`.
   */
  if (value == undefined) {
    return none();
  } else if (isMaybe(value)) {
    return value;
  } else {
    return some(value);
  }
};

export const maybe = {
  some,
  none,
  map,
  from,
  toRef,
  is: {
    some: isSome,
    none: isNone,
  },
};
